import React, { useState, useMemo, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import PartnerContainer from './PartnerContainer';
import { AccountBox as AccountBoxIcon, Person as PersonIcon } from '@mui/icons-material';
import { Switch, FormControlLabel } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { PARTNER_QUERY, PARTNER_TYPES_QUERY, SYSTEM_PARTNERS_QUERY } from '../queries';
import { INSURANCE_TYPES_QUERY } from '../../../../queries/insuranceDocumentQueries';
import { SAVE_PARTNER } from '../mutations.js';
import { useMutation } from 'features/common/security/hooks';
import { useSubscription } from '@apollo/client';
import { useToast, useParams, useDictionaryHandling, useSimpleError, useQueryWithSimpleErrorHandling } from 'hooks';
import { INSURED_SAVED, CLIENT_SAVED, PARTNERS_UPDATED } from '../subscriptions.js';

const PartnersContainer = ({ partners, documentId, financingType, insuranceTypeId, globalReadOnly, dirtyInfoRef, setDirtyInfoRef }) => {
  const [insuredIsClient, setInsuredIsClient] = useState(
    Boolean(partners.insuredId) && Boolean(partners.clientId) && partners.insuredId === partners.clientId
  );
  const [insuredIsClientIsDisabled, setInsuredIsClientIsDisabled] = useState(false);
  const [insuredIsClientIsDirty, setInsuredIsClientIsDirty] = useState(false);
  const [clientSaving, setClientSaving] = useState(false);
  const [insuredSaving, setInsuredSaving] = useState(false);
  const [draw, setDraw] = useState({ insured: true, client: !insuredIsClient });
  const insuredRef = React.createRef();
  const [insuredData, setInsuredData] = useState(null);

  const financingTypeConfigurationJSON = useParams('FinancingTypeConfiguration');
  const defaultPartnerType = useParams('DefaultPartnerType');

  const financingTypeConfiguration = useMemo(() => {
    if (financingTypeConfigurationJSON) return JSON.parse(financingTypeConfigurationJSON);
  }, [financingTypeConfigurationJSON]);

  const { getCodeById: getInsuranceTypeCode } = useDictionaryHandling(INSURANCE_TYPES_QUERY, 'insuranceTypes');
  const insuranceTypeCode = useMemo(() => getInsuranceTypeCode(insuranceTypeId), [getInsuranceTypeCode, insuranceTypeId]);

  useEffect(() => {
    if (financingType && financingTypeConfiguration) {
      const configuration = financingTypeConfiguration.find(x => x.FinancingTypeCode === financingType);
      setInsuredIsClientIsDisabled(configuration?.ClientIsInsuredYN);
      setInsuredIsClientIsDirty(false);
    }
  }, [financingType, financingTypeConfiguration]);

  const { getIdByCode: getPartnerTypeIdByCode } = useDictionaryHandling(PARTNER_TYPES_QUERY, 'partnerTypes');

  const defaultPartnerTypeId = useMemo(() => {
    return getPartnerTypeIdByCode(defaultPartnerType);
  }, [getPartnerTypeIdByCode, defaultPartnerType]);

  const getDefaultPartner = useCallback(() => {
    return {
      partnerId: null,
      partnerTypeId: defaultPartnerTypeId,
      agreesWithPersonalDataUse: false
    };
  }, [defaultPartnerTypeId]);

  const addToast = useToast();
  const { t } = useTranslation();

  const { loading: insuredLoading } = useQueryWithSimpleErrorHandling(PARTNER_QUERY, {
    variables: { partnerId: partners.insuredId },
    skip: !partners.insuredId,
    onCompleted: data => {
      setInsuredData(data?.partner);
    },
    notifyOnNetworkStatusChange: true
  });

  const [clientData, setclientData] = useState(null);

  const { loading: clientLoading } = useQueryWithSimpleErrorHandling(PARTNER_QUERY, {
    variables: { partnerId: partners.clientId },
    skip: !partners.clientId || partners?.insuredId === partners?.clientId,
    onCompleted: data => {
      setclientData(data?.partner);
      setDraw({ ...draw, client: true });
    },
    notifyOnNetworkStatusChange: true
  });

  const { loading: systemPartnersLoading, data: systemPartners } = useQueryWithSimpleErrorHandling(SYSTEM_PARTNERS_QUERY);

  const handleInsuredIsClientChange = event => {
    const { checked } = event.target;
    setInsuredIsClient(checked);
    setInsuredIsClientIsDirty(true);
    if (!checked) {
      setclientData(getDefaultPartner());
    }
    setDraw({ ...draw, client: !checked });
  };

  const showError = useSimpleError();

  const [savePartner] = useMutation(SAVE_PARTNER, {
    onError: error => {
      setInsuredSaving(false);
      setClientSaving(false);
      showError(error);
    }
  });

  const handleSave = (isInsured, isClient) => partner => {
    const input = { documentId, isInsured, isClient, partner };

    if (isInsured === true || (isInsured === true && isClient === true)) {
      setInsuredSaving(true);
    }

    if (isClient === true && isInsured === false) {
      setClientSaving(true);
    }

    savePartner({
      variables: {
        input
      }
    });
    setInsuredIsClientIsDirty(false);
  };

  useSubscription(INSURED_SAVED, {
    variables: { documentId },
    skip: !documentId,
    onData: ({ data: { data } }) => {
      if (data.insuredSaved) {
        setInsuredData(data.insuredSaved?.partner);
        const { silentMode } = data.insuredSaved;
        if (!silentMode) {
          addToast(t('General.Saved'), 'success', { toastId: 'insuredSavedToast' });
        }
      }
      setInsuredSaving(false);
    }
  });

  useSubscription(CLIENT_SAVED, {
    variables: { documentId },
    skip: !documentId,
    onData: ({ data: { data } }) => {
      if (data.clientSaved) {
        setclientData(data.clientSaved?.partner);
        const { silentMode } = data.clientSaved;
        if (!silentMode) {
          addToast(t('General.Saved'), 'success', { toastId: 'clientSavedToast' });
        }
      }
      setClientSaving(false);
    }
  });

  useSubscription(PARTNERS_UPDATED, {
    onData: ({ data: { data } }) => {
      const { partners } = data.partnersUpdated;
      if (partners === null) return;

      partners.forEach(partner => {
        if (partner.partnerId === insuredData?.partnerId) {
          setInsuredData({ ...partner });
        }

        if (partner.partnerId === clientData?.partnerId) {
          setclientData({ ...partner });
        }
      });
    }
  });

  useEffect(() => {
    const insuredIsClient = partners?.insuredId === partners?.clientId;
    setInsuredIsClient(insuredIsClient);
    setDraw(prevDraw => ({ ...prevDraw, client: !insuredIsClient }));
  }, [partners.clientId, partners.insuredId]);

  const InsuredCustomContent = () => (
    <FormControlLabel
      control={
        <Switch
          id='Insured.InsuredIsClient'
          checked={insuredIsClient}
          onChange={handleInsuredIsClientChange}
          color='primary'
          disabled={insuredIsClientIsDisabled || globalReadOnly({ controlName: 'InsuredClient' })}
        />
      }
      label={t('Partner.InsuredIsClient')}
    />
  );

  const InsuredSummaryCustomContent = () =>
    insuredIsClient && <span id='Insured.Summary.InsuredIsClient'>{`• ${t('Partner.InsuredIsClient')}`}</span>;

  return (
    <>
      {draw.insured && (
        <PartnerContainer
          title={'Insured'}
          icon={AccountBoxIcon}
          insuredIsClientIsDirty={insuredIsClientIsDirty}
          systemPartners={systemPartners?.systemPartners}
          partner={insuredData}
          loading={insuredLoading || systemPartnersLoading}
          reference={insuredRef}
          onSave={handleSave(true, insuredIsClient)}
          saving={insuredSaving}
          CustomContent={{ BodyExtension: InsuredCustomContent, SummaryExtension: InsuredSummaryCustomContent }}
          insuranceTypeCode={insuranceTypeCode}
          globalReadOnly={globalReadOnly}
          dirtyInfoRef={dirtyInfoRef}
          setDirtyInfoRef={setDirtyInfoRef}
        />
      )}
      {draw.client && (
        <PartnerContainer
          title={'Client'}
          icon={PersonIcon}
          systemPartners={systemPartners?.systemPartners}
          partner={clientData}
          loading={clientLoading || systemPartnersLoading}
          disabled={insuredIsClient}
          onSave={handleSave(false, true)}
          saving={clientSaving}
          insuranceTypeCode={insuranceTypeCode}
          globalReadOnly={globalReadOnly}
          dirtyInfoRef={dirtyInfoRef}
          setDirtyInfoRef={setDirtyInfoRef}
        />
      )}
    </>
  );
};

PartnersContainer.propTypes = {
  partners: PropTypes.object,
  insuranceTypeId: PropTypes.number,
  documentId: PropTypes.number.isRequired,
  financingType: PropTypes.string,
  globalReadOnly: PropTypes.func.isRequired,
  dirtyInfoRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })]).isRequired,
  setDirtyInfoRef: PropTypes.func.isRequired
};

export default PartnersContainer;
